import { useEffect, useMemo, useRef, useState } from "react";

import { useWorkspaceActiveCustomFields } from "@web-app/api";
import {
  executeAnalyticsQuery,
  executeSQLQuery,
  transformCubeDataToChartFormat,
} from "@web-app/services/analytics";
import type {
  BasicChartData,
  Chart,
  ChartDataUnion,
  GaugeData,
  MetricData,
  TableData,
} from "@web-app/types";

// Import all chart components
import AreaChart from "../charts/AreaChart";
import BarChart from "../charts/BarChart";
import ChartWrapper from "../charts/ChartWrapper";
import DonutChart from "../charts/DonutChart";
import GaugeChart from "../charts/GaugeChart";
import LineChart from "../charts/LineChart";
import MetricChart from "../charts/MetricChart";
import PieChart from "../charts/PieChart";
import TableChart from "../charts/TableChart";
import {
  getTransformableCustomFieldSlugs,
  transformSQLQuery,
} from "../utils/customFieldQuery";

interface ChartRendererProps {
  chart: Chart;
  dateRange?: { from: string; to: string };
}

// Create a stable key for query-related properties only
function getQueryKey(chart: Chart, dateRange?: { from: string; to: string }) {
  return JSON.stringify({
    cubeQuery: chart.cubeQuery,
    dateRange,
    queryType: chart.queryType,
    sqlQuery: chart.sqlQuery,
    type: chart.type,
  });
}

export default function ChartRenderer({
  chart,
  dateRange,
}: ChartRendererProps) {
  const [data, setData] = useState<ChartDataUnion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch custom fields for SQL transformation
  const { customFields } = useWorkspaceActiveCustomFields();

  // Get list of custom field slugs for transformation
  const customFieldSlugs = useMemo(
    () => getTransformableCustomFieldSlugs(customFields || []),
    [customFields],
  );

  // Track the previous query key to avoid unnecessary API calls
  const prevQueryKeyRef = useRef<string | null>(null);
  const queryKey = getQueryKey(chart, dateRange);

  useEffect(() => {
    // Only fetch if query-related properties changed
    if (prevQueryKeyRef.current !== queryKey) {
      prevQueryKeyRef.current = queryKey;
      loadChartData();
    }
  }, [queryKey]);

  const loadChartData = async () => {
    try {
      setLoading(true);
      setError(null);

      let cubeResponse;
      let queryConfig = null;

      if (chart.queryType === "sql" && chart.sqlQuery) {
        // Transform SQL query to include fully qualified custom field names
        const transformedSQL = transformSQLQuery(
          chart.sqlQuery.sql,
          customFieldSlugs,
        );

        // Execute SQL query with date range from dashboard
        cubeResponse = await executeSQLQuery({
          ...chart.sqlQuery,
          dateFrom: dateRange?.from,
          dateTo: dateRange?.to,
          sql: transformedSQL, // Use transformed SQL
        });
      } else if (chart.cubeQuery) {
        // Merge date range into chart's query
        let timeDimensions;

        if (
          chart.cubeQuery.timeDimensions &&
          chart.cubeQuery.timeDimensions.length > 0
        ) {
          // Update existing time dimensions with new date range
          timeDimensions = chart.cubeQuery.timeDimensions.map((td) => ({
            ...td,
            dateRange: dateRange
              ? [dateRange.from, dateRange.to]
              : td.dateRange,
          }));
        } else if (dateRange) {
          // No time dimension in saved chart, but we have a date range from dashboard
          // Add a time filter using scheduled_start_time
          timeDimensions = [
            {
              dateRange: [dateRange.from, dateRange.to],
              dimension: "orders.scheduled_start_time",
            },
          ];
        } else {
          // No time dimensions and no date range from dashboard
          timeDimensions = chart.cubeQuery.timeDimensions;
        }

        const query = {
          ...chart.cubeQuery,
          timeDimensions,
        };

        // Execute Cube.js query
        cubeResponse = await executeAnalyticsQuery(query);
        queryConfig = query;
      } else {
        throw new Error("No query configuration found");
      }

      // Transform response to chart-specific format
      const transformed = transformCubeDataToChartFormat(
        cubeResponse,
        chart.type,
        queryConfig,
      );

      setData(transformed);
    } catch (err) {
      console.error("Failed to load chart data:", err);
      setError("Failed to load chart data");
    } finally {
      setLoading(false);
    }
  };

  // Apply axis label overrides reactively so changes reflect immediately in preview
  const displayData = useMemo(() => {
    if (!data) return null;
    if (
      chart.config &&
      (chart.config.xAxisLabel || chart.config.yAxisLabel) &&
      (data as BasicChartData).axisLabels !== undefined
    ) {
      const basicData = { ...data } as BasicChartData;
      basicData.axisLabels = {
        x: (chart.config.xAxisLabel as string) || basicData.axisLabels?.x,
        y: (chart.config.yAxisLabel as string) || basicData.axisLabels?.y,
      };
      return basicData as ChartDataUnion;
    }
    return data;
  }, [data, chart.config]);

  // Loading state
  if (loading) {
    return (
      <ChartWrapper title={chart.title}>
        <div className="flex items-center justify-center h-full">
          <div className="text-sm text-gray-500">Loading...</div>
        </div>
      </ChartWrapper>
    );
  }

  // Error state
  if (error || !displayData) {
    return (
      <ChartWrapper title={chart.title}>
        <div className="flex flex-col items-center justify-center h-full">
          <div className="text-sm text-red-500 mb-2">
            {error || "No data available"}
          </div>
          <button
            onClick={loadChartData}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            Retry
          </button>
        </div>
      </ChartWrapper>
    );
  }

  // Render appropriate chart component based on type
  switch (chart.type) {
    case "metric":
      return (
        <MetricChart title={chart.title} data={displayData as MetricData} />
      );

    case "bar":
      return (
        <BarChart
          title={chart.title}
          direction="horizontal"
          data={displayData as BasicChartData}
        />
      );

    case "line":
      return (
        <LineChart title={chart.title} data={displayData as BasicChartData} />
      );

    case "area":
      return (
        <AreaChart title={chart.title} data={displayData as BasicChartData} />
      );

    case "pie": {
      const pieData = { ...(displayData as BasicChartData) };
      // Apply custom slice labels from config if available
      if (chart.config?.columnLabels) {
        const customLabels = chart.config.columnLabels as Record<
          string,
          string
        >;
        pieData.labels = pieData.labels.map(
          (label, index) => customLabels[`slice_${index}`] || label,
        );
      }
      return <PieChart title={chart.title} data={pieData} />;
    }

    case "donut": {
      const donutData = { ...(displayData as BasicChartData) };
      // Apply custom slice labels from config if available
      if (chart.config?.columnLabels) {
        const customLabels = chart.config.columnLabels as Record<
          string,
          string
        >;
        donutData.labels = donutData.labels.map(
          (label, index) => customLabels[`slice_${index}`] || label,
        );
      }
      return <DonutChart title={chart.title} data={donutData} />;
    }

    case "gauge":
      return <GaugeChart title={chart.title} data={displayData as GaugeData} />;

    case "table": {
      const tableData = { ...(displayData as TableData) };
      // Apply custom column labels from config if available
      if (chart.config?.columnLabels) {
        const customLabels = chart.config.columnLabels as Record<
          string,
          string
        >;
        tableData.columns = tableData.columns.map((col) => ({
          ...col,
          label: customLabels[col.key] || col.label,
        }));
      }
      return <TableChart title={chart.title} data={tableData} />;
    }

    case "column":
      return (
        <BarChart
          title={chart.title}
          direction="vertical"
          data={displayData as BasicChartData}
        />
      );

    default:
      return (
        <ChartWrapper title={chart.title}>
          <div className="flex items-center justify-center h-full">
            <div className="text-sm text-gray-500">
              Unsupported chart type: {chart.type}
            </div>
          </div>
        </ChartWrapper>
      );
  }
}
